import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { Clause, DataClassification, DemandAnalysis, SubscriptionLevel, Workplace } from '../../api/schemas/schema'
import { getClausesByDemandModuleId, getDefaultValues } from '../../helpers/demandAssessmentHelper'
import { enumValuesToOptions } from '../../helpers/enumHelper'
import { GridItem } from '../common/grid/GridItem'
import { ControlledDropDown } from '../common/inputs/ControlledDropdown'
import { ControlledSimpleDropdown } from '../common/inputs/ControlledSimpleDropdown'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { ControlledTextInput, TextInput } from '../common/inputs/TextInput'
import { UseFormReturn } from 'react-hook-form'
import { DataContext } from '../../context/DataContext'
import { IComplianceState } from '../../interfaces/IComplianceState'
import { Grid } from '../common/grid/Grid'
import { ModalActions } from '../common/modal/Modal'
import { ControlledWorkplaceDropdown } from '../common/inputs/ControlledWorkplaceDropdown'
import { formatDate } from '../../helpers/stringHelper'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'
import { ControlledClauseDropdown } from '../common/inputs/ControlledClauseDropdown'
import { ControlledCheckbox } from '../common/inputs/ControlledCheckbox'

export type DemandAssessmentFormValues = DemandAnalysis & {
   demandComplianceModuleIds: string
   selectedClauses: Clause[]
   onlyDemandModule: boolean
}

type DemandAssessmentFormProps = {
   demandAssessmentForm: UseFormReturn<DemandAssessmentFormValues>
   setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>
   complianceState: IComplianceState
   nextStep: () => void
   workplaces: Workplace[]
}
export const DemandAssessmentForm = ({ demandAssessmentForm, setComplianceState, complianceState, nextStep, workplaces }: DemandAssessmentFormProps) => {
   const dataContext = useContext(DataContext)
   const { organisation } = dataContext.state
   const { subscriptions } = organisation
   const {
      control,
      watch,
      reset,
      setValue,
      getValues,
      formState: { isSubmitting, isDirty },
   } = demandAssessmentForm
   const { processes } = complianceState
   const { demandComplianceModules } = complianceState
   const selectedModuleId = watch('demandComplianceModuleIds')
   const [clausesLoading, setClausesLoading] = useState(false)
   const [clausesOptions, setClausesOptions] = useState<Clause[]>([])

   const processesOptions = useMemo(() => {
      const availableIds = organisation.organisationProcessIds ?? []

      return processes.filter((p) => availableIds.includes(p.id)).map((x) => x.text)
   }, [processes, organisation.organisationProcessIds])

   const demandComplianceModulesOptions = useMemo(
      () =>
         demandComplianceModules.map((m) => ({
            key: m.id,
            text: m.name,
         })),
      [demandComplianceModules]
   )

   const subscriptionLevels = enumValuesToOptions(SubscriptionLevel)
   const demandLevelOptions = useMemo(() => {
      return subscriptionLevels.filter((sl) => subscriptions.includes(Number(sl.key)) && Number(sl.key) !== SubscriptionLevel.ADD_ON)
   }, [subscriptions, subscriptionLevels])

   const demandLevel = watch('demandLevel')
   const isStrategic = demandLevel === SubscriptionLevel.STRATEGIC_DEMAND_ASSESSMENT
   const isSandbox = organisation.isSandbox === true

   const isWorkplaceDisabled = organisation.workplaceLimit === 0
   const defaultWorkplace = workplaces.find((wp) => wp.isDefault)

   useEffect(() => {
      if (isStrategic || isSandbox) {
         setValue('processes', [])
         setValue('demandComplianceModuleIds', '')
         setValue('selectedClauses', [])
      }
   }, [isStrategic, isSandbox, setValue])

   useEffect(() => {
      if (processesOptions.length > 0 && (demandAssessmentForm.getValues('processes') ?? []).length === 0) {
         setValue('processes', processesOptions)
      }
   }, [processesOptions, setValue, demandAssessmentForm])

   useEffect(() => {
      setValue('workplace', defaultWorkplace ? defaultWorkplace : null)
   }, [defaultWorkplace, demandAssessmentForm, setValue])

   useEffect(() => {
      const fetchClauses = async () => {
         if (selectedModuleId) {
            const clauses = await getClausesByDemandModuleId(selectedModuleId, dataContext, setClausesLoading)

            setClausesOptions(clauses)
            setValue('selectedClauses', clauses)
         } else {
            setClausesOptions([])
            setValue('selectedClauses', [])
         }
      }
      fetchClauses()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedModuleId])

   return (
      <Grid gap="L">
         <GridItem size="12/12">
            <ControlledTextInput required control={control} name={'name'} label={t('AssessmentName')} />
         </GridItem>

         <GridItem size="12/12">
            <ControlledAccountPicker required label={t('Owner')} control={control} name={'analysisOwner'} watch={watch} setValue={setValue} />
         </GridItem>
         <GridItem size="12/12">
            <ControlledWorkplaceDropdown
               required
               control={control}
               name={'workplace'}
               label={t('Workplace')}
               options={workplaces}
               disabled={isWorkplaceDisabled}
               showTooltip={isWorkplaceDisabled}
               tooltipMsg={t('FeatureRequiresUpgrade')}
            />
         </GridItem>
         <GridItem size="12/12">
            <ControlledDropDown required control={control} name={'demandLevel'} label={t('DemandLevel')} options={demandLevelOptions} />
         </GridItem>
         {!isStrategic && !isSandbox && (
            <>
               <GridItem size="12/12">
                  <ControlledSimpleDropdown
                     required
                     name={'processes'}
                     control={control}
                     label={t('Process')}
                     options={processesOptions}
                     disabled={isStrategic}
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledDropDown
                     name={'demandComplianceModuleIds'}
                     control={control}
                     label={t('AdditionalModule')}
                     options={demandComplianceModulesOptions}
                     deselectable
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledClauseDropdown
                     control={control}
                     name={'selectedClauses'}
                     label={t('Clauses')}
                     options={clausesOptions}
                     disabled={!selectedModuleId || clausesOptions.length === 0 || clausesLoading}
                     showTooltip={!selectedModuleId || clausesOptions.length === 0 || clausesLoading}
                     tooltipMsg={
                        !selectedModuleId
                           ? t('ChooseAnAdditionalModuleToEnableThisField')
                           : clausesOptions.length === 0
                           ? t('TheAdditionalModuleDoesNotHaveClauses')
                           : ''
                     }
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledCheckbox
                     name={'onlyDemandModule'}
                     control={control}
                     label={t('OnlyAdditionalModule')}
                     showTooltip
                     tooltipMsg={!selectedModuleId ? t('ChooseAnAdditionalModuleToEnableThisField') : t('OnlyAdditionalModuleTooltip')}
                     disabled={!selectedModuleId}
                  />
               </GridItem>
            </>
         )}
         <GridItem size="12/12">
            <ControlledDropDown
               required
               control={control}
               name={'dataClassification'}
               label={t('InformationManagement')}
               options={enumValuesToOptions(DataClassification)}
            />
         </GridItem>
         <GridItem size="6/12">
            <TextInput
               required
               label={t('StartOfAssessment')}
               value={formatDate(getValues('analysisStart'))}
               tooltipMsg={t('ValueCannotBeChanged')}
               showTooltip
               disabled
            />
         </GridItem>
         <GridItem size="6/12">
            <ControlledDatePicker
               required
               control={control}
               name={'analysisEnd'}
               label={t('EndOfAssessment')}
               getValues={getValues}
               compareWith={'analysisStart'}
            />
         </GridItem>
         <GridItem size="12/12">
            <ControlledTextInput multiline control={control} name={'comment'} label={t('Comment')} />
         </GridItem>
         <GridItem size="12/12">
            <ModalActions>
               <Button
                  onClick={() => {
                     setComplianceState((prev) => ({ ...prev, open: false }))
                     reset(getDefaultValues(dataContext, complianceState))
                  }}
                  appearance="secondary"
               >
                  {t('Cancel')}
               </Button>
               {isSandbox || isStrategic || processes.length === 0 ? (
                  <Button type="submit" appearance="primary" disabled={isSubmitting}>
                     {t('CreateAssessment')}
                  </Button>
               ) : (
                  <Button appearance="primary" onClick={nextStep} disabled={!isDirty}>
                     {t('Next')}
                  </Button>
               )}
            </ModalActions>
         </GridItem>
      </Grid>
   )
}
